/**
 * SectionCard - Consistent card layout with title, icon, and optional action
 *
 * Used for major configuration sections, feature cards, and grouped content.
 * Provides hover effects and consistent styling across Vortx apps.
 *
 * @example
 * ```jsx
 * import { SectionCard } from 'vortx-shared-ui/data-display';
 * import { ElectricBolt } from '@mui/icons-material';
 *
 * <SectionCard
 *   title="Grid Constraints"
 *   icon={<ElectricBolt />}
 *   action={<Button>Edit</Button>}
 * >
 *   <TextField label="Import Limit (kW)" />
 * </SectionCard>
 * ```
 */

import React from 'react';
import {
    Card,
    CardContent,
    Box,
    Typography,
    useTheme,
} from '@mui/material';
import { vortxColors, vortxTypography } from '../core/index.js';
import { useThemeMode } from '../theme-mode/index.js';

/**
 * SectionCard Component
 *
 * @param {Object} props
 * @param {string} props.title - Section title
 * @param {React.ReactNode} props.icon - Icon element (automatically colored gold)
 * @param {React.ReactNode} props.children - Section content
 * @param {React.ReactNode} props.action - Optional action element (right side of header)
 * @param {Object} props.sx - Additional card styles
 */
function SectionCard({
    title,
    icon,
    children,
    action,
    sx = {},
}) {
    const { isRetro } = useThemeMode();
    const theme = useTheme();

    // Retro-aware colors
    const retroBg = '#0d1614';
    const retroBorder = '#b28c50';
    const retroTeal = '#5fd4c4';

    return (
        <Card
            sx={{
                borderRadius: isRetro ? 1 : 2,
                backgroundColor: isRetro ? retroBg : theme.palette.background.paper,
                border: isRetro ? `1px solid ${retroBorder}` : 'none',
                boxShadow: isRetro
                    ? `0 0 10px rgba(178, 140, 80, 0.2)`
                    : '0 2px 8px rgba(0,0,0,0.1)',
                transition: 'all 0.2s',
                '&:hover': {
                    boxShadow: isRetro
                        ? `0 0 16px rgba(178, 140, 80, 0.4)`
                        : '0 4px 16px rgba(0,0,0,0.15)',
                },
                mb: 3,
                ...sx,
            }}
        >
            <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {icon && (
                            <Box sx={{
                                color: isRetro ? retroTeal : vortxColors.gold,
                                mr: 1,
                                display: 'flex',
                                alignItems: 'center',
                                filter: isRetro ? `drop-shadow(0 0 4px ${retroTeal})` : 'none',
                            }}>
                                {icon}
                            </Box>
                        )}
                        <Typography
                            variant="h5"
                            sx={{
                                fontFamily: isRetro ? vortxTypography.mono : vortxTypography.heading,
                                color: isRetro ? retroTeal : 'text.primary',
                                fontWeight: 600,
                                textShadow: isRetro ? `0 0 8px ${retroTeal}40` : 'none',
                            }}
                        >
                            {title}
                        </Typography>
                    </Box>
                    {action && <Box>{action}</Box>}
                </Box>
                {children}
            </CardContent>
        </Card>
    );
}

export default SectionCard;
